import React, { useState, useCallback } from 'react';

const INTENT_TYPE_CONFIG = {
  rsvp: {
    title: 'New RSVP Intent',
    buttonLabel: 'RSVP',
    displayHeading: "You're registered!",
    questionPlaceholder: 'Join us for our upcoming event!',
  },
  checkin: {
    title: 'New Check-In Intent',
    buttonLabel: 'Check In',
    displayHeading: "You're checked in!",
    questionPlaceholder: 'Welcome! Check in to get started.',
  },
  walk_in: {
    title: 'New Walk-In Intent',
    buttonLabel: 'Register',
    displayHeading: "You're registered!",
    questionPlaceholder: 'Welcome! Register to join the event.',
  },
  signup_request: {
    title: 'New Signup Request Intent',
    buttonLabel: 'Sign Up Now',
    displayHeading: "Thanks for your interest!",
    questionPlaceholder: 'Interested in becoming a guide? Sign up today!',
  },
  event_rsvp: {
    title: 'New Event RSVP Intent',
    buttonLabel: 'RSVP',
    displayHeading: "You're in!",
    questionPlaceholder: 'Join us for our upcoming event!',
  },
  event_checkin: {
    title: 'New Event Check-In Intent',
    buttonLabel: 'Check In',
    displayHeading: "You're checked in!",
    questionPlaceholder: 'Welcome to the event! Check in here.',
  },
  calendar_booking: {
    title: 'New Calendar Booking Intent',
    buttonLabel: 'Book a Time',
    displayHeading: "You're booked!",
    questionPlaceholder: 'Schedule a time to chat with one of our local experts.',
  },
};

async function copyToClipboard(text, setToast) {
  try {
    await navigator.clipboard.writeText(text);
    setToast('Copied to clipboard');
    setTimeout(() => setToast(null), 2000);
  } catch {
    setToast('Copy failed');
    setTimeout(() => setToast(null), 3000);
  }
}

export default function ActionIntentDialog({ intentType, onSubmit, onCancel, onToast, availableTypes, onTypeChange, existingIntent }) {
  const isEdit = !!existingIntent;
  const typeConfig = INTENT_TYPE_CONFIG[intentType] || INTENT_TYPE_CONFIG.rsvp;
  const existing = existingIntent || {};
  const existingConfig = existing.config || {};
  const existingOption = (existing.options || [])[0] || {};

  const [label, setLabel] = useState(isEdit ? (existing.intentCode?.label || '') : '');
  const [questionText, setQuestionText] = useState(isEdit ? (existingConfig.question_text || '') : '');
  const [buttonLabel, setButtonLabel] = useState(isEdit ? (existingOption.button_label || typeConfig.buttonLabel) : typeConfig.buttonLabel);
  const [displayHeading, setDisplayHeading] = useState(isEdit ? (existingOption.display_heading || typeConfig.displayHeading) : typeConfig.displayHeading);
  const [displayBody, setDisplayBody] = useState(isEdit ? (existingOption.display_body || '') : '');
  const [engagementPoints, setEngagementPoints] = useState(isEdit ? (existingOption.engagement_points ?? 5) : 5);
  const [forwardHeading, setForwardHeading] = useState(isEdit ? (existingConfig.forward_capture_heading || '') : "Someone thought you'd find this interesting — that's fantastic!");
  const [forwardBody, setForwardBody] = useState(isEdit ? (existingConfig.forward_capture_body || '') : 'Drop your email and name to stay in the loop.');
  const [forwardCtaText, setForwardCtaText] = useState(isEdit ? (existingConfig.forward_capture_cta_text || '') : '');
  const [forwardCtaUrl, setForwardCtaUrl] = useState(isEdit ? (existingConfig.forward_capture_cta_url || '') : '');
  const [forwardDismissMessage, setForwardDismissMessage] = useState(isEdit ? (existingConfig.forward_capture_dismiss_message || '') : "We understand. It's too soon.\nBrowse the site and see if we are a good fit.");
  const [showForwardCapture, setShowForwardCapture] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [createdResult, setCreatedResult] = useState(null);

  const validate = useCallback(() => {
    if (!questionText.trim()) { return 'Heading text is required'; }
    if (!buttonLabel.trim()) { return 'Button label is required'; }
    if (!displayHeading.trim()) { return 'Confirmation heading is required'; }
    return null;
  }, [questionText, buttonLabel, displayHeading]);

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    setSubmitting(true);
    const supportsForwardCapture = !['calendar_booking', 'checkin', 'event_checkin', 'walk_in', 'rsvp', 'event_rsvp'].includes(intentType);
    try {
      if (isEdit) {
        const payload = {
          label: label || undefined,
          question_text: questionText.trim(),
          ...(supportsForwardCapture && {
            forward_capture_heading: forwardHeading || undefined,
            forward_capture_body: forwardBody || undefined,
            forward_capture_cta_text: forwardCtaText.trim() || undefined,
            forward_capture_cta_url: forwardCtaUrl.trim() || undefined,
            forward_capture_dismiss_message: forwardDismissMessage.trim() || undefined,
          }),
          options: [{
            response_value: existingOption.response_value || intentType,
            button_label: buttonLabel.trim(),
            display_order: 0,
            engagement_points: parseInt(engagementPoints, 10) || 0,
            display_heading: displayHeading.trim(),
            display_body: displayBody.trim() || undefined,
          }],
        };
        await onSubmit(payload);
        if (onToast) { onToast('Intent updated'); }
        onCancel();
      } else {
        const payload = {
          intent_type: intentType,
          label: label || undefined,
          question_text: questionText.trim(),
          button_label: buttonLabel.trim(),
          engagement_points: parseInt(engagementPoints, 10) || 0,
          display_heading: displayHeading.trim(),
          display_body: displayBody.trim() || undefined,
          ...(supportsForwardCapture && {
            forward_capture_heading: forwardHeading || undefined,
            forward_capture_body: forwardBody || undefined,
            forward_capture_cta_text: forwardCtaText.trim() || undefined,
            forward_capture_cta_url: forwardCtaUrl.trim() || undefined,
            forward_capture_dismiss_message: forwardDismissMessage.trim() || undefined,
          }),
        };
        const result = await onSubmit(payload);
        setCreatedResult(result?.data || result);
      }
    } catch {
      if (isEdit && onToast) { onToast('Failed to update intent', 'error'); }
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px', width: '100%' };
  const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' };

  if (createdResult) {
    const code = createdResult.intentCode?.code || '';
    const baseUrl = `${window.location.origin}/i/${code}`;
    const cidToken = '{{contact.hs_object_id}}';
    const actionUrl = `${baseUrl}?v=${intentType}&ref=${cidToken}`;
    const pageUrl = `${baseUrl}?ref=${cidToken}`;
    const emailUrl = `https://outdoorguides.com/i/${code}?cid=${cidToken}`;

    return (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
          maxWidth: '600px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          maxHeight: '90vh', overflowY: 'auto',
        }}>
          <h3 style={{ margin: '0 0 4px', fontSize: '18px' }}>Intent Created</h3>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280' }}>
            Code: <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{code}</span>
            {' '}&mdash; {intentType.toUpperCase()}
          </p>

          <div style={{ marginBottom: '16px' }}>
            <div style={labelStyle}>Page URL (shows event details + {buttonLabel} button)</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input readOnly value={pageUrl} style={{ ...inputStyle, backgroundColor: '#f9fafb', fontFamily: 'monospace', fontSize: '11px' }} />
              <button onClick={() => copyToClipboard(pageUrl, onToast)} style={{
                padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db',
                backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap',
              }}>Copy</button>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={labelStyle}>Direct Action URL (auto-submits {intentType})</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input readOnly value={actionUrl} style={{ ...inputStyle, backgroundColor: '#f9fafb', fontFamily: 'monospace', fontSize: '11px' }} />
              <button onClick={() => copyToClipboard(actionUrl, onToast)} style={{
                padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db',
                backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap',
              }}>Copy</button>
            </div>
          </div>

          {intentType === 'event_rsvp' && (
            <div style={{ marginBottom: '16px' }}>
              <div style={labelStyle}>Email URL (paste into HubSpot email button)</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input readOnly value={emailUrl} style={{ ...inputStyle, backgroundColor: '#f9fafb', fontFamily: 'monospace', fontSize: '11px' }} data-testid="email-url-input" />
                <button onClick={() => copyToClipboard(emailUrl, onToast)} style={{
                  padding: '6px 12px', borderRadius: '4px', border: '1px solid #009263',
                  backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap',
                  color: '#009263',
                }} data-testid="copy-email-url">Copy</button>
              </div>
            </div>
          )}

          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px', marginBottom: '16px' }}>
            Replace {'{{contact.hs_object_id}}'} with the recipient{"'"}s contact ID.
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={onCancel} style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none',
              backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer',
              fontSize: '14px', fontWeight: 600,
            }}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
        maxWidth: '560px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>{isEdit ? 'Edit Intent' : typeConfig.title}</h3>

        {isEdit && (
          <div style={{ marginBottom: '12px', padding: '6px 10px', backgroundColor: '#f3f4f6', borderRadius: '6px', fontSize: '12px', color: '#6b7280' }}>
            Type: <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{intentType.replace(/_/g, ' ')}</span>
            {existing.intentCode?.code && <> &middot; Code: <code style={{ fontWeight: 600 }}>{existing.intentCode.code}</code></>}
          </div>
        )}

        {!isEdit && availableTypes && availableTypes.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Intent Type *</label>
            <select style={inputStyle} value={intentType} onChange={(e) => onTypeChange(e.target.value)}>
              {availableTypes.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        )}

        {validationError && (
          <div role="alert" style={{
            padding: '8px 12px', backgroundColor: '#fee2e2', color: '#dc2626',
            borderRadius: '6px', fontSize: '13px', marginBottom: '12px',
          }}>{validationError}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Label</label>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Happy Hour RSVP — Denver" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Heading Text *</label>
            <textarea value={questionText} onChange={(e) => setQuestionText(e.target.value)}
              placeholder={typeConfig.questionPlaceholder} rows={2}
              style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div>
              <label style={labelStyle}>Button Label *</label>
              <input type="text" value={buttonLabel} onChange={(e) => setButtonLabel(e.target.value)}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Engagement Points</label>
              <input type="number" value={engagementPoints} onChange={(e) => setEngagementPoints(e.target.value)}
                style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Confirmation Heading *</label>
            <input type="text" value={displayHeading} onChange={(e) => setDisplayHeading(e.target.value)}
              style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Confirmation Body</label>
            <input type="text" value={displayBody} onChange={(e) => setDisplayBody(e.target.value)}
              placeholder="Optional follow-up text" style={inputStyle} />
          </div>

          {intentType === 'signup_request' && (
            <div style={{ padding: '8px 12px', backgroundColor: '#f5f3ff', borderRadius: '6px', border: '1px solid #ddd6fe' }}>
              <div style={labelStyle}>Destination URL (auto-generated)</div>
              <input readOnly value="/guides/signup" style={{ ...inputStyle, backgroundColor: '#ede9fe', fontFamily: 'monospace', fontSize: '12px' }} />
              <div style={{ fontSize: '11px', color: '#7c3aed', marginTop: '4px' }}>
                UTM params from the campaign channel will be appended automatically.
              </div>
            </div>
          )}

          {!['calendar_booking', 'checkin', 'event_checkin', 'walk_in', 'rsvp', 'event_rsvp'].includes(intentType) && (
            <details open={showForwardCapture} onToggle={(e) => setShowForwardCapture(e.target.open)}>
              <summary style={{ fontSize: '13px', fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                Forward Capture Settings
              </summary>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                <div>
                  <label style={labelStyle}>Heading</label>
                  <input type="text" value={forwardHeading} onChange={(e) => setForwardHeading(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Body</label>
                  <textarea value={forwardBody} onChange={(e) => setForwardBody(e.target.value)}
                    rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <div>
                  <label style={labelStyle}>Dismiss Message</label>
                  <textarea value={forwardDismissMessage} onChange={(e) => setForwardDismissMessage(e.target.value)}
                    rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <label style={labelStyle}>Dismiss CTA Text</label>
                    <input type="text" value={forwardCtaText} onChange={(e) => setForwardCtaText(e.target.value)}
                      placeholder="Learn more" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Dismiss CTA URL</label>
                    <input type="text" value={forwardCtaUrl} onChange={(e) => setForwardCtaUrl(e.target.value)}
                      placeholder="https://..." style={inputStyle} />
                  </div>
                </div>
              </div>
            </details>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
            backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px',
          }}>Cancel</button>
          <button
            disabled={submitting}
            onClick={handleSubmit}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none',
              backgroundColor: intentType === 'rsvp' ? '#3b82f6' : intentType === 'walk_in' ? '#f97316' : intentType === 'signup_request' ? '#8b5cf6' : '#10b981',
              color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: 600, opacity: submitting ? 0.6 : 1,
            }}
          >{submitting ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Intent')}</button>
        </div>
      </div>
    </div>
  );
}
